import React, { useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  ShoppingBag,
  MessageCircle,
  Minus,
  Plus,
  ChevronRight,
  BadgeCheck,
  Truck,
  Gift,
  Check,
} from "lucide-react";
import { formatSGD, waLink } from "../lib/mockData";
import { useProduct, useCategory, useProductsByCategory } from "../hooks/useCatalog";
import { useSeo } from "../lib/seo";
import { useCart } from "../lib/cartContext";
import { toast } from "sonner";
import ProductCard from "../components/common/ProductCard";
import { OrnamentDivider } from "../components/common/Ornaments";

export default function ProductDetails() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [qty, setQty] = useState(1);
  const [activeImg, setActiveImg] = useState(0);

  const { data: product, isLoading } = useProduct(slug);
  const { data: category } = useCategory(product?.category);
  const { data: categoryProducts = [] } = useProductsByCategory(product?.category);

  // Per-product SEO comes from the admin-managed seo fields in Firestore.
  useSeo({
    title: product?.seo?.metaTitle || product?.name,
    description: product?.seo?.metaDesc || product?.shortDesc,
    path: product ? `/product/${product.seo?.urlSlug || product.slug}` : undefined,
    image: product?.images?.[0],
  });

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 lg:gap-14 animate-pulse">
          <div className="aspect-square rounded-2xl bg-cream-2 border border-gold/20" />
          <div className="space-y-5">
            <div className="h-6 w-40 bg-cream-2 rounded" />
            <div className="h-12 w-3/4 bg-cream-2 rounded" />
            <div className="h-8 w-32 bg-cream-2 rounded" />
            <div className="h-24 w-full bg-cream-2 rounded" />
            <div className="h-12 w-full bg-cream-2 rounded" />
          </div>
        </div>
        <div className="mt-10 text-center text-sm text-emerald-light/70">Loading…</div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center">
        <h1 className="font-serif text-4xl text-emerald">Product Not Found</h1>
        <Link to="/shop" className="mt-6 inline-block bg-emerald text-cream px-6 py-3 text-sm">
          Back to Shop
        </Link>
      </div>
    );
  }

  const price = product.discountPrice ?? product.price;
  const hasDiscount = !!product.discountPrice;
  const related = categoryProducts.filter((p) => p.id !== product.id).slice(0, 3);
  const outOfStock = product.stock === 0;

  const onAdd = () => {
    if (outOfStock) return;
    addItem(product.id, qty);
    toast.success(`${qty} × ${product.name} added to cart`);
  };

  const onBuyNow = () => {
    if (outOfStock) return;
    addItem(product.id, qty);
    navigate("/cart");
  };

  return (
    <div data-testid={`product-details-${product.id}`}>
      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
        <div className="flex items-center gap-2 text-xs text-emerald-light/70">
          <Link to="/" className="hover:text-gold">Home</Link>
          <ChevronRight size={12} />
          <Link to="/shop" className="hover:text-gold">Shop</Link>
          <ChevronRight size={12} />
          {category && (
            <>
              <Link to={`/category/${category.slug}`} className="hover:text-gold">{category.name}</Link>
              <ChevronRight size={12} />
            </>
          )}
          <span className="text-emerald">{product.name}</span>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 lg:gap-14">
          {/* Gallery */}
          <div>
            <div className="aspect-square rounded-2xl overflow-hidden border border-gold/30 bg-cream-2 p-4">
              <img
                src={product.images[activeImg]}
                alt={product.name}
                className="w-full h-full object-contain"
                data-testid="product-main-image"
              />
            </div>
            {product.images.length > 1 && (
              <div className="mt-4 grid grid-cols-4 gap-3">
                {product.images.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveImg(i)}
                    className={`aspect-square rounded-lg overflow-hidden border-2 bg-cream-2 p-1.5 transition-all ${
                      activeImg === i ? "border-gold" : "border-transparent hover:border-gold/40"
                    }`}
                    data-testid={`product-thumb-${i}`}
                  >
                    <img src={img} alt="" className="w-full h-full object-contain" loading="lazy" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <span className="bg-emerald text-cream text-[10px] px-2 py-1 uppercase tracking-widest font-semibold">
                Halal-Friendly
              </span>
              {product.bestSeller && (
                <span className="bg-gold text-emerald text-[10px] px-2 py-1 uppercase tracking-widest font-semibold">
                  Best Seller
                </span>
              )}
            </div>
            <h1 className="font-serif text-4xl md:text-5xl text-emerald leading-tight">{product.name}</h1>
            <div className="text-xs uppercase tracking-widest text-emerald-light/70 mt-2">
              SKU: {product.sku}
            </div>

            <div className="mt-6 flex items-baseline gap-3">
              <span className="font-serif text-4xl text-emerald">{formatSGD(price)}</span>
              {hasDiscount && (
                <span className="text-lg text-emerald-light/60 line-through">
                  {formatSGD(product.price)}
                </span>
              )}
              {hasDiscount && (
                <span className="bg-gold/20 text-gold text-xs font-semibold px-2 py-1 rounded">
                  Save {formatSGD(product.price - product.discountPrice)}
                </span>
              )}
            </div>

            <p className="mt-5 text-emerald-light/85 leading-relaxed">{product.description}</p>

            {/* Stock */}
            <div className="mt-6 flex items-center gap-2 text-sm">
              <BadgeCheck size={16} className={outOfStock ? "text-red-600" : "text-emerald"} />
              <span className="font-medium text-emerald">
                {outOfStock
                  ? "Out of Stock"
                  : product.stock < 6
                  ? `Only ${product.stock} left in stock`
                  : "In Stock"}
              </span>
            </div>

            {/* Qty + CTA */}
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <div className="inline-flex items-center border border-gold/40 rounded-lg overflow-hidden">
                <button
                  onClick={() => setQty(Math.max(1, qty - 1))}
                  className="px-4 py-3 hover:bg-gold/10 transition-colors"
                  aria-label="decrease"
                  data-testid="qty-decrease-btn"
                >
                  <Minus size={14} />
                </button>
                <div className="px-5 py-3 min-w-[3rem] text-center font-medium" data-testid="qty-value">
                  {qty}
                </div>
                <button
                  onClick={() => setQty(qty + 1)}
                  className="px-4 py-3 hover:bg-gold/10 transition-colors"
                  aria-label="increase"
                  data-testid="qty-increase-btn"
                >
                  <Plus size={14} />
                </button>
              </div>
              <button
                onClick={onAdd}
                disabled={outOfStock}
                className="flex-1 inline-flex items-center justify-center gap-2 bg-emerald text-cream px-6 py-3 text-sm font-medium hover:bg-emerald-dark transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid={`add-to-cart-btn-${product.id}`}
              >
                <ShoppingBag size={16} /> Add to Cart
              </button>
              <button
                onClick={onBuyNow}
                disabled={outOfStock}
                className="flex-1 inline-flex items-center justify-center gap-2 bg-gold text-emerald px-6 py-3 text-sm font-semibold hover:bg-gold-light transition-colors disabled:opacity-40"
                data-testid={`buy-now-btn-${product.id}`}
              >
                Buy Now
              </button>
            </div>

            <a
              href={waLink(`Hi HALALGIFHAMPERS, I'd like to enquire about "${product.name}" (${product.sku}).`)}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 w-full inline-flex items-center justify-center gap-2 border border-gold text-emerald px-6 py-3 text-sm font-medium hover:bg-gold/10 transition-colors"
              data-testid="product-whatsapp-enquiry"
            >
              <MessageCircle size={16} /> Enquire via WhatsApp
            </a>

            {/* Info blocks */}
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
              <div className="flex items-center gap-2 p-3 bg-cream-2 rounded-lg">
                <Truck size={16} className="text-gold" /> Islandwide Delivery
              </div>
              <div className="flex items-center gap-2 p-3 bg-cream-2 rounded-lg">
                <Gift size={16} className="text-gold" /> Gift Message Included
              </div>
              <div className="flex items-center gap-2 p-3 bg-cream-2 rounded-lg">
                <BadgeCheck size={16} className="text-gold" /> Halal-Friendly Selection
              </div>
            </div>
          </div>
        </div>

        {/* Included Items */}
        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="bg-white border border-gold/25 rounded-xl p-8">
            <h3 className="font-serif text-2xl text-emerald mb-4">What's Included</h3>
            <ul className="space-y-2.5">
              {product.items.map((i, idx) => (
                <li key={idx} className="flex items-start gap-2.5 text-sm text-emerald-light/90">
                  <Check size={16} className="text-gold mt-0.5 shrink-0" />
                  <span>{i}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-emerald text-cream rounded-xl p-8">
            <h3 className="font-serif text-2xl text-gold mb-4">Halal & Delivery Notes</h3>
            <ul className="space-y-3 text-sm text-cream/85">
              {product.halalInfo && (
                <li className="flex gap-2.5"><Check size={16} className="text-gold mt-0.5 shrink-0" /> {product.halalInfo}</li>
              )}
              {product.deliveryInfo && (
                <li className="flex gap-2.5"><Check size={16} className="text-gold mt-0.5 shrink-0" /> {product.deliveryInfo}</li>
              )}
              <li className="flex gap-2.5"><Check size={16} className="text-gold mt-0.5 shrink-0" /> All food items are halal-friendly and sourced with care.</li>
              <li className="flex gap-2.5"><Check size={16} className="text-gold mt-0.5 shrink-0" /> Islandwide Singapore delivery — pick your preferred date at checkout.</li>
              <li className="flex gap-2.5"><Check size={16} className="text-gold mt-0.5 shrink-0" /> Add a personalised gift message at checkout.</li>
              <li className="flex gap-2.5"><Check size={16} className="text-gold mt-0.5 shrink-0" /> Contents may vary slightly based on seasonal availability.</li>
            </ul>
          </div>
        </div>

        {/* Related */}
        {related.length > 0 && (
          <div className="mt-20">
            <OrnamentDivider className="text-gold mb-8" />
            <h3 className="font-serif text-3xl md:text-4xl text-emerald text-center">
              You may also <span className="italic text-gold">love</span>
            </h3>
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {related.map((p, i) => (
                <ProductCard key={p.id} product={p} index={i} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
